'use client';

import { useHeaderContext } from '@jouzen/ecom-components/layout';
import { Button } from '@jouzen/ecom-components/v3/Button';
import { LayoutGrid } from '@jouzen/ecom-components/v3/LayoutGrid';
import { Typography } from '@jouzen/ecom-components/v3/Typography';
import { useGateValue } from '@statsig/react-bindings';
import { cx } from 'class-variance-authority';
import { useTranslations } from 'next-intl';
import type { ComponentType, ReactNode } from 'react';
import { useEffect } from 'react';
import type { MessageKey } from 'types/LocaleMessageKey';

import ImagesSlideOverOnScroll from '@/app/[locale]/about-us/_components/ImagesSlideOverOnScroll';
import ScrollTeaser from '@/app/[locale]/about-us/_components/ScrollTeaser';
import Image from '@/app/components/Image';
import LandingPageHero from '@/app/components/LegacyLandingPage/LandingPageHero';
import RichText from '@/app/components/RichText';
import SvgIconOutlineAltitude from '@/app/icons/IconOutlineAltitude';
import SvgIconOutlineCheck from '@/app/icons/IconOutlineCheck';
import SvgIconOutlineEye from '@/app/icons/IconOutlineEye';
import SvgIconOutlineHuman from '@/app/icons/IconOutlineHuman';
import SvgIconOutlineWe from '@/app/icons/IconOutlineWe';

import SLIDE_IMAGES from './data/slide-images';
import { STAT_LINES, STAT_LINES_UPDATED } from './data/stats';

interface StatLineProps {
  readonly figure: MessageKey;
  readonly caption: MessageKey;
}

const ourValues: [MessageKey, MessageKey, ComponentType][] = [
  ['about_us_not_me', 'about_us_not_me_copy', SvgIconOutlineWe],
  ['about_us_human_first', 'about_us_human_first_copy', SvgIconOutlineHuman],
  [
    'about_us_trust_matters',
    'about_us_trust_matters_copy',
    SvgIconOutlineCheck,
  ],
  ['about_us_aim_higher', 'about_us_aim_higher_copy', SvgIconOutlineAltitude],
  [
    'about_us_we_find_solutions',
    'about_us_we_find_solutions_copy',
    SvgIconOutlineEye,
  ],
];

const tValues = {
  i: (chunks: ReactNode) => (
    <em className="block pl-20 font-serif italic md:pl-40">{chunks}</em>
  ),
};

const StatLine = ({ figure, caption }: StatLineProps) => {
  const t = useTranslations();
  return (
    <div
      className={cx(
        'gap-4 md:flex md:items-center md:justify-between',
        'col-main grid md:col-start-4 md:col-end-22',
        'pb-5 md:py-10',
        'border-b border-gray-400',
      )}
    >
      <Typography
        variant="h1"
        Element="strong"
        font="serif"
        className="pr-4 font-extralight tracking-tight"
      >
        {t(figure)}
      </Typography>
      <Typography
        Element="span"
        variant="h6"
        className="text-sandstone-500 md:text-right"
      >
        {t(caption)}
      </Typography>
    </div>
  );
};

const Content = () => {
  const t = useTranslations();
  const { setInverse } = useHeaderContext()!;

  useEffect(() => {
    setInverse(false);
    return () => setInverse(false);
  }, [setInverse]);

  const Stats = useGateValue('oura_stat_roll_out')
    ? STAT_LINES_UPDATED
    : STAT_LINES;

  return (
    <>
      <ScrollTeaser content="scroll_to_discover" outline />
      <LandingPageHero
        useFullHeight={false}
        title="about_us_hero_title"
        subtitle="about_us_hero_copy"
        imgSrc="about-us/hero-d-img2x"
        imgAlt="reproductive_health_hero_alt"
        className="pt-0 text-sandstone-500 md:pt-0 lg:min-h-0"
        copyClassNames="col-start-2 mt-4"
      />

      <LayoutGrid>
        <div className="col-main mt-12 mb-20 md:col-start-7 md:col-end-17 md:mt-0 lg:col-end-21">
          <Typography Element="p" variant="h6">
            {t('about_us_secondary_copy')}
          </Typography>
        </div>
      </LayoutGrid>
      <ImagesSlideOverOnScroll
        containerStyles="-my-32 pt-6 md:pt-3 md:-my-[7.5rem] lg:pt-0 lg:-my-32"
        listStyles="px-[80vw]"
        wrapperStyles="md:pt-25 pb-40 md:pb-48"
        Heading={
          <Typography
            Element="h2"
            variant="h1"
            className={cx(
              'mb-20 md:mb-0',
              'col-main',
              'md:col-start-4 md:col-end-22',
              'z-10', // so it sits on top of the images list, below
            )}
          >
            <RichText messageKey="about_us_secondary_title" tValues={tValues} />
          </Typography>
        }
        images={SLIDE_IMAGES}
      />
      <LayoutGrid className="md:pt-24">
        {Stats.map((stat) => (
          <StatLine key={stat.id} figure={stat.figure} caption={stat.caption} />
        ))}
        <div className="col-main mt-8 mb-4 md:col-start-4 md:col-end-12 md:my-0 md:pt-8">
          <Image
            src="about-us/about-us-data"
            width={566}
            height={348}
            alt={t('about_us_medical_alt')}
          />
          <Typography Element="h4" variant="h4" className="mt-6 mb-3">
            {t('about_us_data_title')}
          </Typography>
          <Typography Element="p" variant="base">
            {t('about_us_data_copy')}
          </Typography>
        </div>
        <div className="col-main md:col-start-14 md:col-end-22 md:mt-56 md:pt-8">
          <Image
            src="about-us/medical-advisory-board"
            width={566}
            height={348}
            alt={t('about_us_medical_alt')}
          />
          <Typography Element="h4" variant="h4" className="mt-6 mb-3">
            {t('about_us_medical_title')}
          </Typography>
          <Typography Element="p" variant="base">
            {t('about_us_medical_copy')}
          </Typography>
          <Button
            variant="secondary-dark"
            className="mt-10"
            href="/medical-advisory-board"
          >
            {t('learn_more')}
          </Button>
        </div>
      </LayoutGrid>
      <div className="mt-16 border-b border-gray-100 bg-gray-100 py-24">
        <LayoutGrid>
          <Typography
            variant="h3"
            Element="h2"
            className="col-main lg:col-start-4 lg:col-end-9"
          >
            {t('our_values')}
          </Typography>
          {ourValues.map(([title, copy, Icon]) => (
            <div
              key={title}
              className="col-main mb-8 lg:odd:col-start-16 lg:odd:col-end-22 lg:even:col-start-10 lg:even:col-end-15"
            >
              <Icon />
              <Typography variant="h6" className="py-3">
                {t(title)}
              </Typography>
              <Typography>{t(copy)}</Typography>
            </div>
          ))}
        </LayoutGrid>
      </div>
    </>
  );
};

export default Content;
